import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { LayoutDashboard, Package, FolderTree, Boxes, ShoppingCart, Ticket, MessageSquare, Users, Stethoscope, FileText, LayoutTemplate, Receipt, History, Settings, LogOut, Loader2, Store, ClipboardCheck, Images, Layers, ShieldCheck, UserCog, ScrollText, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LOGO_LIGHT_URL } from "@/lib/brand";
import { ADMIN_ROLES } from "@/lib/roles";

const APPROVE_PERMS = ["brand.approve", "product.approve", "image.approve", "showcase.approve", "publish.approve", "compliance.manage"];
const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/approval", label: "Approval Centre", icon: ShieldCheck, anyPerm: APPROVE_PERMS },
  { to: "/admin/products", label: "Products", icon: Package, perm: "product.view" },
  { to: "/admin/brands", label: "Brands", icon: Store, perm: "brand.view" },
  { to: "/admin/brand-products", label: "Reviews", icon: ClipboardCheck, perm: "product.view" },
  { to: "/admin/image-reviews", label: "Images", icon: Images, perm: "image.view" },
  { to: "/admin/catalogues", label: "Catalogues", icon: Layers, perm: "product.view" },
  { to: "/admin/categories", label: "Categories", icon: FolderTree, perm: "category.manage" },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes, perm: "product.view" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart, perm: "order.manage" },
  { to: "/admin/invoice-template", label: "Invoice", icon: Receipt, perm: "settings.manage" },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket, perm: "coupon.manage" },
  { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare, perm: "coupon.manage" },
  { to: "/admin/customers", label: "Customers", icon: Users, perm: "customer.manage" },
  { to: "/admin/consultants", label: "Consultants", icon: Stethoscope, perm: "consultant.manage" },
  { to: "/admin/blogs", label: "Blog", icon: FileText, perm: "content.manage" },
  { to: "/admin/website", label: "Website", icon: LayoutTemplate, perm: "content.manage" },
  { to: "/admin/activity", label: "Activity", icon: History, perm: "activity.view" },
  { to: "/admin/users", label: "Admin Users", icon: UserCog, perm: "user.manage" },
  { to: "/admin/roles", label: "Roles & Permissions", icon: ShieldCheck, perm: "role.manage" },
  { to: "/admin/audit", label: "Audit Trail", icon: ScrollText, superOnly: true },
  { to: "/admin/settings", label: "Settings", icon: Settings, perm: "settings.manage" },
];

export default function AdminLayout() {
  const { user, authReady, logout } = useApp();
  const nav = useNavigate();
  const [perms, setPerms] = useState(null);
  const [isSuper, setIsSuper] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { if (authReady && (!user || !ADMIN_ROLES.includes(user.role))) nav("/admin/login"); }, [authReady, user, nav]);
  useEffect(() => {
    if (authReady && user && ADMIN_ROLES.includes(user.role)) {
      api.get("/admin/my-permissions").then((r) => { setPerms(new Set(r.data.permissions)); setIsSuper(r.data.is_super_admin); }).catch(() => setPerms(new Set()));
    }
  }, [authReady, user]);
  if (!authReady || !user || perms === null) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;

  const can = (l) => { if (l.superOnly) return isSuper; return isSuper || (!l.perm && !l.anyPerm) || (l.perm && perms.has(l.perm)) || (l.anyPerm && l.anyPerm.some((p) => perms.has(p))); };
  const links = LINKS.filter(can);

  return (
    <div className="min-h-screen bg-cream flex">
      <aside className="w-60 bg-forest text-cream flex-col fixed inset-y-0 hidden lg:flex">
        <Link to="/admin" className="flex items-center gap-2 px-5 h-28 border-b border-cream/10">
          <img src={LOGO_LIGHT_URL} alt="LIVENEAT logo" className="h-20 w-auto object-contain" />
          <div className="text-[10px] tracking-widest text-mustard uppercase">Admin</div>
        </Link>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-auto">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 transition-colors ${isActive ? "bg-cream/15 text-cream" : "text-cream/70 hover:bg-cream/10"}`}>
              <l.icon className="w-4.5 h-4.5" strokeWidth={1.75} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-cream/10">
          <div className="px-3 pb-2 text-[11px] text-cream/50 truncate">{user.username || user.email}</div>
          <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-cream/70 hover:bg-cream/10 w-full"><LogOut className="w-4.5 h-4.5" /> Logout</button>
        </div>
      </aside>
      <div className="flex-1 lg:ml-60 min-w-0 overflow-x-hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <header className="lg:hidden bg-forest text-cream h-14 flex items-center justify-between px-4 sticky top-0 z-40">
            <div className="flex items-center gap-3">
              <SheetTrigger asChild>
                <button data-testid="admin-mobile-menu" aria-label="Open menu" className="-ml-1 p-1"><Menu className="w-6 h-6" /></button>
              </SheetTrigger>
              <span className="font-heading text-lg">LIVENEAT Admin</span>
            </div>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-mobile-logout" aria-label="Logout"><LogOut className="w-5 h-5" /></button>
          </header>
          <SheetContent side="left" className="w-72 bg-forest text-cream border-none p-0 flex flex-col" data-testid="admin-mobile-drawer">
            <SheetTitle className="sr-only">Admin navigation</SheetTitle>
            <div className="px-5 h-24 flex items-center border-b border-cream/10 shrink-0">
              <img src={LOGO_LIGHT_URL} alt="LIVENEAT logo" className="h-16 w-auto object-contain" />
            </div>
            <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setMobileOpen(false)} data-testid={`admin-mnav-${l.label.toLowerCase()}`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-500 transition-colors ${isActive ? "bg-cream/15 text-cream" : "text-cream/70 hover:bg-cream/10"}`}>
                  <l.icon className="w-5 h-5" strokeWidth={1.75} /> {l.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t border-cream/10 shrink-0">
              <div className="px-3 pb-2 text-[11px] text-cream/50 truncate">{user.username || user.email}</div>
              <button onClick={() => { setMobileOpen(false); logout(); nav("/admin/login"); }} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-cream/70 hover:bg-cream/10 w-full"><LogOut className="w-4.5 h-4.5" /> Logout</button>
            </div>
          </SheetContent>
        </Sheet>
        <main className="p-4 lg:p-8 min-w-0 max-w-full"><Outlet /></main>
      </div>
    </div>
  );
}
